// 鶏「終わらぬ鬨」の縦を使う2技 (2026-10-29・作者の答え r1)。
// 作者 (10-29)「Hollow Knight の悪夢のグリム・ラディアンスのような縦を使う技。位置で避けるか判断で弾くか」。
//   暁呼び (roosterDawnCall): 鳴いて真上へ上がり、宙で止まって羽を3回降らせる (1本・1本・3本)。
//     羽は狙った瞬間の主人公の位置へ落ちる床の帯 (弾いても避けても良い)。降りて翼で打つのが active (近い所だけ・弾ける)。
//   鳴き挟み の締め (roosterCrowDrop): 爪2発のあとに鳴いて真上へ上がり、主人公の上まで寄って止まり、床が光った所へ真下に落ちる。
//     落ちる所は止まる瞬間の主人公の位置で決まる (光ってから 0.38 秒＝走って抜けるか、落ちてくる所を弾く)。
//   終幕の鬨 (roosterCrowFinale・鶏専用のとどめ): 崩れた主人公の前で胸を張って長く鳴き上げ、舞い上がって真上から踏みしめる。
//     「鳴けば終われる」と信じる鶏が、来ない朝の代わりに相手を終わらせる。踏んだまま、もう一度鳴く (戻りの絵)。
// 溜め (telegraph) の間だけ、この台本が x・高さ・向きを決める (宙廻り src/rooster_aerial_roam.ts と同じ形)。
// 宙 (高さ > 72) にいる間は主人公の攻撃が当たらない (src/combat.ts)。数値は全て仮。
import type { AttackDef, Boss, HazardSpawn, Player } from './types'
import { PLAYER_DAMAGE } from './balance'

type Bounds = { leftWall: number; rightWall: number }
export type RoosterVertKind = 'roosterDawnCall' | 'roosterCrowDrop' | 'roosterCrowFinale'
type DropKind = Exclude<RoosterVertKind, 'roosterDawnCall'>

/** 暁呼びの定数表 (秒・px)。 */
export const ROOSTER_DAWN = {
  crowSec: 0.40,       // 地上で鳴いて翼を広げる
  riseSec: 0.40,       // 真上へ (easeOut)
  heightPx: 230,
  // 羽を狙う秒 (この瞬間の主人公の x へ帯を置く)。帯は予告 telegraphMs の後に activeMs だけ当たる。
  //   offsets は主人公の x からのずれ。最後の1回は3本で、真ん中を避けても左右に逃げ過ぎると当たる。
  volleys: [
    { atSec: 0.80, offsets: [0] },
    { atSec: 1.20, offsets: [0] },
    { atSec: 1.60, offsets: [-200, 0, 200] },
  ],
  featherWidthPx: 120,
  featherTelegraphMs: 500,
  featherActiveMs: 220,  // 踏み込みの無敵 (200ms) より長く・帯から出るのが答え
  descendAtSec: 2.10,  // ここから降りる (最後の羽が当たり終わる 2.26 と重なる)
  descendSec: 0.35,
} as const
export const ROOSTER_DAWN_CHARGE_MS = Math.round((ROOSTER_DAWN.descendAtSec + ROOSTER_DAWN.descendSec) * 1000)

/** 鳴き挟みの締め (落ちる踏みつけ) の定数表。 */
export const ROOSTER_DROP = {
  crowSec: 0.42,
  riseSec: 0.36,
  heightPx: 280,
  trackSec: 0.40,      // 宙で主人公の上へ寄る (今の主人公の x を追う)
  pauseSec: 0.20,      // 止まって目が光る (落ちる所はこの頭で決まる)
  dropSec: 0.18,       // 真下へ (ease-in)
  xMin: 160,
  xMax: 800,
} as const

/** 終幕の鬨 (鶏の致命) の定数表。主人公は崩れて動かないので、寄りは短く、鳴きを長く。気絶 1800ms の内に必ず落ちる。 */
export const ROOSTER_FINALE = {
  crowSec: 0.50,
  riseSec: 0.24,
  heightPx: 160,      // 1コマの落ちは 30px 以内 (tests/stun_cut.test.ts・高さ÷落ちる秒 ≤ 900)
  trackSec: 0.16,
  pauseSec: 0.14,
  dropSec: 0.18,
  xMin: 0,
  xMax: 10000,
} as const

type DropTable = typeof ROOSTER_DROP | typeof ROOSTER_FINALE
const DROP_TABLES: Record<DropKind, DropTable> = { roosterCrowDrop: ROOSTER_DROP, roosterCrowFinale: ROOSTER_FINALE }
function dropTimes(R: DropTable) {
  const riseAt = R.crowSec
  const trackAt = riseAt + R.riseSec
  const pauseAt = trackAt + R.trackSec
  const fallAt = pauseAt + R.pauseSec
  return { riseAt, trackAt, pauseAt, fallAt, chargeMs: Math.round((fallAt + R.dropSec) * 1000) }
}
const DROP_T = dropTimes(ROOSTER_DROP)
export const ROOSTER_DROP_PAUSE_AT = DROP_T.pauseAt
export const ROOSTER_DROP_FALL_AT = DROP_T.fallAt
export const ROOSTER_DROP_CHARGE_MS = DROP_T.chargeMs
const FINALE_T = dropTimes(ROOSTER_FINALE)
export const ROOSTER_FINALE_PAUSE_AT = FINALE_T.pauseAt
export const ROOSTER_FINALE_FALL_AT = FINALE_T.fallAt
export const ROOSTER_FINALE_CHARGE_MS = FINALE_T.chargeMs

export type RoosterVertLeg = 'crow' | 'rise' | 'hover' | 'track' | 'pause' | 'descend' | 'landed'
export type RoosterVertPlan = { kind?: RoosterVertKind; startX: number; landX?: number }

const easeOutQuad = (p: number): number => 1 - (1 - p) * (1 - p)
const easeInOut = (p: number): number => p < 0.5 ? 2 * p * p : 1 - 2 * (1 - p) * (1 - p)
const clamp01 = (p: number): number => Math.max(0, Math.min(1, p))

/** 経過秒 → x・高さ・区間 (純関数)。鳴き挟みの track 区間は今の主人公の x を使う。 */
export function roosterVertPoseAt(kind: RoosterVertKind, t: number, plan: RoosterVertPlan, playerX: number):
  { x: number; heightPx: number; leg: RoosterVertLeg; progress: number } {
  if (kind === 'roosterDawnCall') {
    const D = ROOSTER_DAWN
    const x = plan.startX
    if (t < D.crowSec) return { x, heightPx: 0, leg: 'crow', progress: clamp01(t / D.crowSec) }
    if (t < D.crowSec + D.riseSec) {
      const p = clamp01((t - D.crowSec) / D.riseSec)
      return { x, heightPx: D.heightPx * easeOutQuad(p), leg: 'rise', progress: p }
    }
    if (t < D.descendAtSec) return { x, heightPx: D.heightPx, leg: 'hover', progress: clamp01((t - D.crowSec - D.riseSec) / (D.descendAtSec - D.crowSec - D.riseSec)) }
    if (t < D.descendAtSec + D.descendSec) {
      const p = clamp01((t - D.descendAtSec) / D.descendSec)
      return { x, heightPx: D.heightPx * (1 - p * p), leg: 'descend', progress: p }
    }
    return { x, heightPx: 0, leg: 'landed', progress: 1 }
  }
  const R = DROP_TABLES[kind]
  const T = dropTimes(R)
  if (t < T.riseAt) return { x: plan.startX, heightPx: 0, leg: 'crow', progress: clamp01(t / R.crowSec) }
  if (t < T.trackAt) {
    const p = clamp01((t - T.riseAt) / R.riseSec)
    return { x: plan.startX, heightPx: R.heightPx * easeOutQuad(p), leg: 'rise', progress: p }
  }
  if (t < T.pauseAt && plan.landX === undefined) {
    const p = clamp01((t - T.trackAt) / R.trackSec)
    return { x: plan.startX + (playerX - plan.startX) * easeInOut(p), heightPx: R.heightPx, leg: 'track', progress: p }
  }
  const landX = plan.landX ?? playerX
  if (t < T.fallAt) return { x: landX, heightPx: R.heightPx, leg: 'pause', progress: clamp01((t - T.pauseAt) / R.pauseSec) }
  if (t < T.fallAt + R.dropSec) {
    const p = clamp01((t - T.fallAt) / R.dropSec)
    return { x: landX, heightPx: R.heightPx * (1 - p * p), leg: 'descend', progress: p }
  }
  return { x: landX, heightPx: 0, leg: 'landed', progress: 1 }
}

function isVert(atk: AttackDef | null | undefined): atk is AttackDef & { choreography: RoosterVertKind } {
  return atk?.choreography === 'roosterDawnCall' || atk?.choreography === 'roosterCrowDrop' || atk?.choreography === 'roosterCrowFinale'
}
function dropKindOf(boss: Boss): DropKind | null {
  const c = boss.currentAttack?.choreography
  return boss.state === 'telegraph' && (c === 'roosterCrowDrop' || c === 'roosterCrowFinale') ? c : null
}
function inVertTelegraph(boss: Boss): boolean {
  return boss.state === 'telegraph' && isVert(boss.currentAttack)
}

function dropRoom(kind: RoosterVertKind, bounds: Bounds): { lo: number; hi: number } {
  const R = kind === 'roosterDawnCall' ? ROOSTER_DROP : DROP_TABLES[kind]
  return { lo: Math.max(R.xMin, bounds.leftWall), hi: Math.min(R.xMax, bounds.rightWall) }
}

/** telegraph の毎tick、踏み込み処理の代わりに呼ぶ (boss.ts)。 */
export function advanceRoosterVertical(boss: Boss, player: Player, atk: AttackDef, bounds: Bounds): Boss {
  const kind = atk.choreography as RoosterVertKind
  const t = boss.stateElapsed
  // 技が替わったら (鳴き挟みの途中で崩れてとどめへ) 前の技の出発点を持ち越さない。
  const plan0 = boss.roosterVert?.kind === kind ? boss.roosterVert : { startX: boss.x }
  const { lo, hi } = dropRoom(kind, bounds)
  const aimX = Math.max(lo, Math.min(hi, player.x))
  let landX = plan0.landX
  if (kind !== 'roosterDawnCall' && landX === undefined && t >= dropTimes(DROP_TABLES[kind]).pauseAt) landX = aimX
  const plan: RoosterVertPlan = { kind, startX: plan0.startX, ...(landX !== undefined ? { landX } : {}) }
  const pose = roosterVertPoseAt(kind, t, plan, aimX)
  // 宙 (暁呼び・鳴き挟み) で主人公が崩れた時は、とどめの鳴きの間に今の高さから降りる (1コマで床へ跳ばない・tests/stun_cut.test.ts)。
  if (kind === 'roosterCrowFinale' && boss.finisherDropFromPx && pose.leg === 'crow') {
    pose.heightPx = boss.finisherDropFromPx * (1 - pose.progress * pose.progress)
  }
  // 向きは主人公を見る (落ちる区間は決まった向きのまま)。
  const facing: Boss['facing'] = pose.leg === 'descend' || pose.leg === 'landed' || Math.abs(player.x - pose.x) < 4
    ? boss.facing : player.x > pose.x ? 'right' : 'left'
  return {
    ...boss, roosterVert: plan, facing,
    x: Math.max(bounds.leftWall, Math.min(bounds.rightWall, pose.x)),
    altitudePx: Math.max(0, pose.heightPx),
  }
}

/** 溜めを離れたら片付ける (boss.ts の毎tickの頭)。 */
export function settleRoosterVertical(boss: Boss): Boss {
  if (!boss.roosterVert || inVertTelegraph(boss)) return boss
  const { roosterVert: _r, ...rest } = boss
  return rest
}

/** 羽の帯 (1回ぶん)。spawnZoneHits へ渡す合成の技。 */
export function roosterFeatherAttack(offsets: readonly number[]): AttackDef {
  const D = ROOSTER_DAWN
  const hazardSpawns: HazardSpawn[] = offsets.map((offset) => ({
    anchor: 'player', offset, width: D.featherWidthPx, telegraphMs: D.featherTelegraphMs, activeMs: D.featherActiveMs,
    damage: PLAYER_DAMAGE.standard, postureDamage: 22, hitOnce: true, kind: 'light', parryable: true,
  }))
  return {
    id: 'roosterFeather', label: '羽', kind: 'area', chargeMs: D.featherTelegraphMs, activeMs: D.featherActiveMs, recoveryMs: 0,
    parryable: true, defense: 'parry', range: 0, damage: PLAYER_DAMAGE.standard, postureDamage: 22,
    sourceBossId: 'inverted-jester', hazardSpawns,
  }
}

/** 前tick→今tickの間に羽を狙う秒を越えた回 (game.ts が主人公の今の x で帯を出す)。 */
export function roosterFeathersDue(before: Boss, after: Boss): (readonly number[])[] {
  if (!inVertTelegraph(after) || after.currentAttack?.choreography !== 'roosterDawnCall') return []
  const t0 = before.state === 'telegraph' && before.currentAttack === after.currentAttack ? before.stateElapsed : -Infinity
  const t1 = after.stateElapsed
  return ROOSTER_DAWN.volleys.filter(v => t0 < v.atSec && t1 >= v.atSec).map(v => v.offsets)
}

/** 落ちる直前の目の白い光 (描画用・鳴き挟みの締めだけ)。 */
export function roosterDropGlint(boss: Boss): { color: 'white' | 'red'; progress: number } | null {
  const kind = dropKindOf(boss)
  if (!kind) return null
  const R = DROP_TABLES[kind]
  const T = dropTimes(R)
  const t = boss.stateElapsed
  if (t < T.pauseAt || t >= T.fallAt) return null
  // とどめは弾けない＝赤。
  return { color: kind === 'roosterCrowFinale' ? 'red' : 'white', progress: (t - T.pauseAt) / R.pauseSec }
}

/** 光る瞬間が始まったら true (予兆音)。 */
export function roosterDropGlintStarted(before: Boss, after: Boss): boolean {
  const kind = dropKindOf(after)
  if (!kind || before.currentAttack !== after.currentAttack) return false
  const pauseAt = dropTimes(DROP_TABLES[kind]).pauseAt
  const t0 = before.state === 'telegraph' ? before.stateElapsed : -Infinity
  return t0 < pauseAt && after.stateElapsed >= pauseAt
}

/** 落ちる床の光 (描画用)。寄る間は薄く主人公の下、止まってからは決まった所を濃く。 */
export function roosterDropPreview(boss: Boss, playerX: number): { x: number; alpha: number } | null {
  const kind = dropKindOf(boss)
  if (!kind) return null
  const t = boss.stateElapsed
  if (t < dropTimes(DROP_TABLES[kind]).trackAt) return null
  const landX = boss.roosterVert?.landX
  if (landX === undefined) return { x: playerX, alpha: 0.15 }
  return { x: landX, alpha: 0.6 }
}
